<template>
  <!-- 信标日志弹窗 -->
  <a-modal
    :title="`信标日志 - ${beaconName || ''}`"
    :visible="visible"
    @cancel="handleClose"
    :width="1200"
    :footer="null"
  >
    <!-- 条件搜索区域 -->
    <div class="table-page-search-wrapper">
      <a-form layout="inline">
        <a-row :gutter="[16, 8]">
          <!-- 时间筛选 -->
          <a-col :xxl="6" :xl="8" :lg="8" :md="8" :sm="24" :xs="8">
            <a-form-item label="时间">
              <a-date-picker
                v-model:value="queryParam.operationTime"
                style="width: 100%"
                format="YYYY-MM-DD"
                valueFormat="YYYY-MM-DD"
                placeholder="请选择时间"
                allow-clear
                class="no-border-radius-picker"
              />
            </a-form-item>
          </a-col>
          <!-- 事由筛选 -->
          <a-col :xxl="6" :xl="8" :lg="8" :md="8" :sm="24" :xs="8">
            <a-form-item label="事由">
              <a-input
                v-model:value="queryParam.operationReason"
                placeholder="请输入事由"
                allow-clear
                class="no-border-radius-input"
              />
            </a-form-item>
          </a-col>
          <!-- 操作人筛选 -->
          <a-col :xxl="6" :xl="8" :lg="8" :md="8" :sm="24" :xs="8">
            <a-form-item label="操作人">
              <a-input
                v-model:value="queryParam.operationName"
                placeholder="请输入操作人"
                allow-clear
                class="no-border-radius-input"
              />
            </a-form-item>
          </a-col>

          <!-- 查询和重置按钮 -->
          <a-col :xxl="6" :xl="8" :lg="8" :md="8" :sm="24" :xs="8">
            <a-form-item>
              <span class="table-page-search-submitButtons">
                <a-button type="primary" @click="handleQuery" class="search-button"> 搜索 </a-button>
                <a-button style="margin-left: 8px" @click="handleReset" class="no-border-button"> 重置 </a-button>
              </span>
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </div>
    <!-- 操作按钮区域 -->
    <div class="table-operations">
      <a-button
        type="text"
        shape="circle"
        @click="handleRefresh"
        v-hasPermi="refreshPermission"
        class="refresh-icon-button"
      >
        <img src="@/assets/images/projectUser/refresh.png" alt="刷新" />
      </a-button>
      <a-button
        @click="handleExport"
        :disabled="!list || list.length === 0 || pagination.total === 0"
        v-hasPermi="exportPermission"
        class="no-border-button"
      >
        <UploadOutlined />导出
      </a-button>
    </div>
    <!-- 数据表格 -->
    <div v-if="list && list.length > 0">
      <a-table
        :columns="tableColumns"
        :data-source="list"
        :pagination="false"
        :loading="loading"
        :row-selection="{
          selectedRowKeys: selectedRowKeys,
          onChange: onSelectChange,
        }"
        rowKey="id"
        class="optimized-table"
        @change="handleTableChange"
      >
        <!-- 使用 bodyCell 插槽自定义单元格显示 -->
        <template #bodyCell="{ column, record }">
          <!-- 时间列：格式化显示 -->
          <template v-if="column.dataIndex === 'operationTime'">
            <span>
              {{ parseTime(record.operationTime) }}
            </span>
          </template>
          <!-- 备注列：单行显示，最多30个字符，超出省略号，添加 tooltip -->
          <template v-else-if="column.dataIndex === 'operationRemark'">
            <a-tooltip v-if="record.operationRemark" placement="rightBottom">
              <template #title>
                <div class="remark-tooltip-content">
                  <template v-if="splitRemark(record.operationRemark).length > 0">
                    <div
                      v-for="(item, idx) in splitRemark(record.operationRemark)"
                      :key="idx"
                      class="remark-tooltip-item"
                    >
                      {{ item }}
                    </div>
                  </template>
                  <span v-else>{{ record.operationRemark }}</span>
                </div>
              </template>
              <span class="remark-content">
                {{ truncateText(record.operationRemark, 50) }}
              </span>
            </a-tooltip>
            <span v-else>-</span>
          </template>
        </template>
      </a-table>

      <!-- 分页组件 -->
      <div class="pagination-wrapper">
        <div class="pagination-left">
          <span class="pagination-info">
            显示第 {{ (pagination.current - 1) * pagination.pageSize + 1 }} 到第
            {{ Math.min(pagination.current * pagination.pageSize, pagination.total) }} 条数据/共
            {{ pagination.total }} 条
          </span>
          <a-select
            v-model:value="pagination.pageSize"
            style="width: 100px; margin-left: 8px"
            @change="onShowSizeChange"
            class="no-border-radius-select"
          >
            <a-select-option :value="10">10条/页</a-select-option>
            <a-select-option :value="20">20条/页</a-select-option>
            <a-select-option :value="50">50条/页</a-select-option>
            <a-select-option :value="100">100条/页</a-select-option>
          </a-select>
        </div>
        <div class="pagination-right">
          <a-pagination
            :current="pagination.current"
            :total="pagination.total"
            :page-size="pagination.pageSize"
            :show-total="false"
            @change="changeSize"
          />
          <span class="pagination-jump">
            到第
            <a-input-number
              v-model:value="jumpPage"
              :min="1"
              :max="Math.ceil(pagination.total / pagination.pageSize)"
              style="width: 60px; margin: 0 4px"
              @pressEnter="handleJumpPage"
              class="no-border-radius-input-number"
            />
            页
          </span>
        </div>
      </div>
    </div>
    <!-- 空数据提示 -->
    <a-empty v-else description="暂无信标日志数据" style="padding: 60px 0" />
  </a-modal>
</template>

<script>
import { $on, $off, $once, $emit } from '../../../../utils/gogocodeTransfer'
/**
 * 信标日志组件
 * 功能：显示指定信标的操作日志记录
 * - 支持按时间、事由、操作人筛选日志
 * - 支持分页查询和刷新
 * - 支持导出日志数据
 * - 通过props接收信标ID和名称
 */
import { pageBeaconLog } from '@/api/system/beaconLog'
import { tableMixin } from '@/store/table-mixin'
import { UploadOutlined } from '@ant-design/icons-vue'

export default {
  name: 'BeaconLog',
  components: {
    UploadOutlined,
  },
  mixins: [tableMixin],
  props: {
    // 弹窗显示/隐藏状态
    visible: {
      type: Boolean,
      default: false,
    },
    // 信标ID（必填）
    beaconId: {
      type: Number,
      default: null,
    },
    // 信标名称（用于显示在标题中）
    beaconName: {
      type: String,
      default: '',
    },
    // 权限前缀，用于动态生成权限值
    permissionPrefix: {
      type: String,
      default: 'system:beaconLog',
    },
  },
  data() {
    return {
      // 日志列表数据
      list: [],
      // 加载状态
      loading: false,
      // 选中的行key数组
      selectedRowKeys: [],
      // 选中的行数据数组
      selectedRows: [],
      // 查询参数
      queryParam: {
        operationTime: null, // 操作时间
        operationReason: undefined, // 事由
        operationName: undefined, // 操作人
      },
      // 分页配置
      pagination: {
        current: 1, // 当前页码
        pageSize: 10, // 每页条数
        total: 0, // 总条数
      },
      // 分页跳转
      jumpPage: 1,
      // 表格列配置
      columns: [
        {
          title: '序号',
          dataIndex: 'id',
          align: 'center',
          width: 80,
        },
        {
          title: '时间',
          dataIndex: 'operationTime',
          align: 'center',
          width: 180,
        },
        {
          title: '事由',
          dataIndex: 'operationReason',
          align: 'center',
          ellipsis: {
            showTitle: true,
          },
        },
        {
          title: '操作人',
          dataIndex: 'operationName',
          align: 'center',
          width: 120,
          ellipsis: {
            showTitle: true,
          },
        },
        {
          title: '备注',
          dataIndex: 'operationRemark',
          align: 'left',
          width: 400,
          className: 'remark-column-cell',
        },
      ],
    }
  },
  computed: {
    /**
     * 刷新权限
     * 根据权限前缀动态生成刷新权限值
     */
    refreshPermission() {
      return [`${this.permissionPrefix}:refresh`]
    },
    /**
     * 导出权限
     * 根据权限前缀动态生成导出权限值
     */
    exportPermission() {
      return [`${this.permissionPrefix}:export`]
    },
    /**
     * 表格列配置（使用 computed 以便在 customRender 中访问 this）
     */
    tableColumns() {
      const that = this
      return [
        {
          title: '序号',
          dataIndex: 'id',
          width: 80,
          align: 'center',
          customRender: ({ index }) => {
            const current = that.pagination.current || 1
            const pageSize = that.pagination.pageSize || 10
            const currentIndex = index !== undefined && index !== null ? Number(index) : 0
            if (isNaN(currentIndex) || isNaN(current) || isNaN(pageSize)) {
              return currentIndex + 1
            }
            return (current - 1) * pageSize + currentIndex + 1
          },
        },
        ...this.columns.slice(1), // 保留其他列配置
      ]
    },
  },
  watch: {
    /**
     * 监听弹窗显示状态
     * 当弹窗打开且信标ID存在时，初始化数据
     */
    visible(newVal) {
      if (newVal && this.beaconId) {
        this.init()
      }
    },
    /**
     * 监听信标ID变化
     * 当信标ID变化且弹窗打开时，重新初始化数据
     */
    beaconId(newVal) {
      if (newVal && this.visible) {
        this.init()
      }
    },
  },
  methods: {
    /**
     * 初始化数据
     * 重置分页、清空列表、重置查询条件，然后获取日志列表
     */
    init() {
      // 重置分页到第一页
      this.pagination.current = 1
      this.pagination.total = 0
      this.jumpPage = 1
      // 清空列表数据
      this.list = []
      // 清空选中状态
      this.selectedRowKeys = []
      this.selectedRows = []
      // 重置查询条件
      this.queryParam = {
        operationTime: null,
        operationReason: undefined,
        operationName: undefined,
      }
      // 获取日志列表
      this.getList()
    },

    /**
     * 获取信标日志列表
     * 根据信标ID和查询条件分页查询日志数据
     */
    getList() {
      // 校验信标ID
      if (!this.beaconId) {
        this.$message.warning('信标ID不能为空', 3)
        return
      }

      // 开始加载
      this.loading = true

      // 构建请求参数
      const params = {
        beaconId: this.beaconId, // 信标ID
        pageNum: this.pagination.current, // 当前页码
        pageSize: this.pagination.pageSize, // 每页条数
        operationTime: this.queryParam.operationTime, // 操作时间
        operationReason: this.queryParam.operationReason, // 事由
        operationName: this.queryParam.operationName, // 操作人
      }

      // 移除空值参数，避免传递无效参数
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === null || params[key] === '') {
          delete params[key]
        }
      })

      // 发送请求
      pageBeaconLog(params)
        .then((response) => {
          // 设置列表数据和总数
          this.list = response.data.records || []
          this.pagination.total = response.data.total || 0
          this.loading = false
        })
        .catch(() => {
          // 请求失败，清空数据
          this.loading = false
          this.list = []
          this.pagination.total = 0
        })
    },

    /**
     * 查询操作
     * 重置到第一页并重新获取列表
     */
    handleQuery() {
      this.pagination.current = 1
      this.jumpPage = 1
      this.getList()
    },

    /**
     * 重置查询条件
     * 清空所有查询条件，重置到第一页并重新获取列表
     */
    handleReset() {
      // 重置查询条件
      this.queryParam = {
        operationTime: null,
        operationReason: undefined,
        operationName: undefined,
      }
      // 重置到第一页
      this.pagination.current = 1
      this.jumpPage = 1
      // 重新获取列表
      this.getList()
    },

    /**
     * 刷新列表
     * 重新获取当前条件下的日志列表
     */
    handleRefresh() {
      this.getList()
    },

    /**
     * 导出日志数据
     * 导出当前查询条件下的所有日志数据（非选中数据）
     */
    handleExport() {
      const that = this
      // 确认对话框
      this.$confirm({
        title: '是否确认导出?',
        content: '此操作将导出当前条件下所有数据而非选中数据',
        onOk() {
          // 构建导出参数
          const params = {
            beaconId: that.beaconId,
            operationTime: that.queryParam.operationTime,
            operationReason: that.queryParam.operationReason,
            operationName: that.queryParam.operationName,
          }
          // 移除空值参数
          Object.keys(params).forEach((key) => {
            if (params[key] === undefined || params[key] === null || params[key] === '') {
              delete params[key]
            }
          })
          // 下载文件
          that.download('system/beacon-log/export', params, `信标日志_${that.beaconName}_${new Date().getTime()}.xlsx`)
        },
        onCancel() {},
      })
    },

    /**
     * 关闭弹窗
     * 触发close事件，通知父组件关闭弹窗
     */
    handleClose() {
      $emit(this, 'close')
    },

    /**
     * 多选变化处理
     * 更新选中的行key和行数据
     * @param {Array} selectedRowKeys - 选中的行key数组
     * @param {Array} selectedRows - 选中的行数据数组
     */
    onSelectChange(selectedRowKeys, selectedRows) {
      this.selectedRowKeys = selectedRowKeys
      this.selectedRows = selectedRows
    },

    /**
     * 表格变化处理
     * 处理分页变化（页码、每页条数）
     * @param {Object} pagination - 分页对象
     */
    handleTableChange(pagination) {
      // 更新分页信息
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      // 分页变化时清空选择
      this.onSelectChange([], [])
      // 重新获取列表
      this.getList()
    },

    /**
     * 分页大小变化处理
     * @param {Number} pageSize - 每页条数
     */
    onShowSizeChange(pageSize) {
      this.pagination.pageSize = pageSize
      this.pagination.current = 1
      this.jumpPage = 1
      this.getList()
    },

    /**
     * 分页页码变化处理
     * @param {Number} page - 页码
     */
    changeSize(page) {
      this.pagination.current = page
      this.jumpPage = page
      this.getList()
    },

    /**
     * 分页跳转处理
     */
    handleJumpPage() {
      if (this.jumpPage && this.jumpPage >= 1) {
        const maxPage = Math.ceil(this.pagination.total / this.pagination.pageSize)
        if (this.jumpPage > maxPage) {
          this.jumpPage = maxPage
        }
        this.pagination.current = this.jumpPage
        this.getList()
      }
    },

    /**
     * 截取文本
     * 将文本截取到指定长度，超出部分显示省略号
     * @param {String} text - 文本内容
     * @param {Number} maxLength - 最大长度
     * @returns {String} 截取后的文本
     */
    truncateText(text, maxLength) {
      if (!text) {
        return '-'
      }
      if (text.length <= maxLength) {
        return text
      }
      return text.substring(0, maxLength) + '...'
    },
    /**
     * 分割备注信息
     * 将备注信息按分号（；或;）分割成数组，用于多行显示
     * @param {String} remark - 备注信息
     * @returns {Array} 分割后的备注数组
     */
    splitRemark(remark) {
      if (!remark) {
        return []
      }
      // 后端使用 "; " (英文分号+空格) 作为分隔符
      // 同时兼容中文分号（；）和英文分号（;），以及带空格和不带空格的情况
      // 先统一替换为统一的分隔符
      let normalized = remark
        .replace(/；\s*/g, '|||SEPARATOR|||') // 中文分号，可能带空格
        .replace(/;\s*/g, '|||SEPARATOR|||') // 英文分号，可能带空格
      // 按分隔符分割，并去除每项首尾空格，过滤空字符串
      return normalized
        .split('|||SEPARATOR|||')
        .map((item) => item.trim())
        .filter((item) => item && item.length > 0)
    },
  },
  emits: ['close'],
}
</script>

<style scoped>
.remark-content {
  text-align: left;
  display: block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

/* Tooltip 样式 */
.remark-tooltip-content {
  max-width: 400px;
  max-height: 300px;
  overflow-y: auto;
  text-align: left;
  /* 隐藏滚动条但保持滚动功能 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
}

.remark-tooltip-content::-webkit-scrollbar {
  display: none; /* Chrome/Safari */
  width: 0;
  height: 0;
}

.remark-tooltip-item {
  display: block;
  line-height: 1.8;
  margin-bottom: 6px;
  word-wrap: break-word;
  word-break: break-all;
  white-space: normal;
}

.remark-tooltip-item:last-child {
  margin-bottom: 0;
}

/* 确保 tooltip 能正确显示全部内容 */
:deep(.ant-tooltip-inner) {
  max-width: 600px !important;
  word-break: break-word !important;
  white-space: pre-wrap !important;
  max-height: 400px;
  overflow-y: auto;
  /* 隐藏滚动条但保持滚动功能 */
  scrollbar-width: none !important; /* Firefox */
  -ms-overflow-style: none !important; /* IE 10+ */
}

:deep(.ant-tooltip-inner)::-webkit-scrollbar {
  display: none !important; /* Chrome/Safari */
  width: 0 !important;
  height: 0 !important;
}

/* 搜索栏样式 */
.table-page-search-wrapper {
  margin-bottom: 16px;
}

/* 移除所有输入框圆角的通用样式 */
.no-border-radius-input :deep(.ant-input) {
  border-radius: 0 !important;
}

.no-border-radius-input :deep(.ant-input-affix-wrapper) {
  border-radius: 0 !important;
}

.no-border-radius-picker :deep(.ant-picker) {
  border-radius: 0 !important;
}

.no-border-radius-select :deep(.ant-select-selector) {
  border-radius: 0 !important;
}

.no-border-radius-input-number :deep(.ant-input-number) {
  border-radius: 0 !important;
}

.no-border-radius-input-number :deep(.ant-input-number-input) {
  border-radius: 0 !important;
}

/* 搜索区域输入框样式覆盖 */
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input),
.table-page-search-wrapper :deep(.ant-form-item-control-input .ant-input) {
  width: 220px !important;
  background: #ffffff !important;
  border-radius: 4px !important;
  border: 1px solid #c7c7c7 !important;
}

/* 带前缀/后缀的输入框外层样式 */
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-affix-wrapper),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper) {
  width: 220px !important;
  background: #ffffff !important;
  border-radius: 4px !important;
  border: 1px solid #c7c7c7 !important;
}

/* 带前缀/后缀的输入框内部input去掉边框，避免嵌套 */
.table-page-search-wrapper :deep(.ant-input-affix-wrapper .ant-input) {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

/* 日期选择器样式 */
.table-page-search-wrapper :deep(.no-border-radius-picker .ant-picker),
.table-page-search-wrapper :deep(.ant-picker) {
  width: 220px !important;
  background: #ffffff !important;
  border-radius: 4px !important;
  border: 1px solid #c7c7c7 !important;
}

/* 下拉选择框样式 */
.table-page-search-wrapper :deep(.no-border-radius-select .ant-select-selector),
.table-page-search-wrapper :deep(.ant-select-selector) {
  width: 220px !important;
  background: #ffffff !important;
  border-radius: 4px !important;
  border: 1px solid #c7c7c7 !important;
}

/* 移除搜索栏输入框、下拉选择框、日期选择框聚焦时的高亮效果 */
/* 输入框聚焦样式 - 内部input隐藏边框 */
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input:focus),
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-focused),
.table-page-search-wrapper :deep(.ant-input:focus),
.table-page-search-wrapper :deep(.ant-input-focused) {
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
}

/* 带前缀/后缀的输入框聚焦样式 - 外部容器保持边框，内部input隐藏边框 */
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-affix-wrapper:focus),
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-affix-wrapper-focused),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper:focus),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper-focused) {
  border: 1px solid #c7c7c7 !important;
  box-shadow: none !important;
  outline: none !important;
}

/* 聚焦时，内部input隐藏边框 */
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-affix-wrapper:focus .ant-input),
.table-page-search-wrapper :deep(.no-border-radius-input .ant-input-affix-wrapper-focused .ant-input),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper:focus .ant-input),
.table-page-search-wrapper :deep(.ant-input-affix-wrapper-focused .ant-input) {
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
}

/* 下拉选择框聚焦样式 */
.table-page-search-wrapper :deep(.no-border-radius-select .ant-select-selector:focus),
.table-page-search-wrapper :deep(.no-border-radius-select .ant-select-focused .ant-select-selector),
.table-page-search-wrapper :deep(.ant-select-selector:focus),
.table-page-search-wrapper :deep(.ant-select-focused .ant-select-selector) {
  border: 1px solid #c7c7c7 !important;
  box-shadow: none !important;
  outline: none !important;
}

/* 日期选择框聚焦样式 */
.table-page-search-wrapper :deep(.no-border-radius-picker .ant-picker:focus),
.table-page-search-wrapper :deep(.no-border-radius-picker .ant-picker-focused),
.table-page-search-wrapper :deep(.ant-picker:focus),
.table-page-search-wrapper :deep(.ant-picker-focused) {
  border: 1px solid #c7c7c7 !important;
  box-shadow: none !important;
  outline: none !important;
}

/* 搜索按钮和新增按钮样式 */
.search-button,
.add-button {
  background-color: #165dff !important;
  border-color: #165dff !important;
  border-radius: 4px !important;
}

.search-button:hover,
.add-button:hover {
  background-color: #4080ff !important;
  border-color: #4080ff !important;
}

.search-button:focus,
.add-button:focus {
  background-color: #165dff !important;
  border-color: #165dff !important;
}

/* 其他按钮圆角设置 */
.table-page-search-wrapper :deep(.ant-btn) {
  border-radius: 4px;
}

/* 按钮区域样式 */
.table-page-search-submitButtons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-operations {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-operations :deep(.ant-btn) {
  border-radius: 4px;
}

.refresh-icon-button {
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.refresh-icon-button img {
  width: 32px;
  height: 32px;
}

/* 去掉指定按钮的边框 */
.no-border-button {
  border: none !important;
  background-color: #f3f3f3 !important;
  color: #101010 !important;
}

.no-border-button:hover,
.no-border-button:focus {
  border: none !important;
  background-color: #f3f3f3 !important;
  color: #101010 !important;
}

/* 分页组件样式 */
.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pagination-left {
  display: flex;
  align-items: center;
}

.pagination-info {
  color: rgba(0, 0, 0, 0.65);
}

.pagination-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.pagination-jump {
  display: flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.65);
}

/* 分页组件样式 - 去掉圆角 */
.pagination-wrapper :deep(.ant-btn) {
  border-radius: 0 !important;
}

.pagination-wrapper :deep(.ant-pagination-item) {
  border-radius: 0 !important;
}

.pagination-wrapper :deep(.ant-pagination-prev),
.pagination-wrapper :deep(.ant-pagination-next),
.pagination-wrapper :deep(.ant-pagination-prev .ant-pagination-item-link),
.pagination-wrapper :deep(.ant-pagination-next .ant-pagination-item-link) {
  border-radius: 0 !important;
}

.pagination-wrapper :deep(.ant-pagination-prev button),
.pagination-wrapper :deep(.ant-pagination-next button) {
  border-radius: 0 !important;
}

.pagination-wrapper :deep(.ant-input-number) {
  border-radius: 0 !important;
}

.pagination-wrapper :deep(.ant-input-number-input) {
  border-radius: 0 !important;
}

/* 分页选中页数样式 */
.pagination-wrapper :deep(.ant-pagination-item-active) {
  background: #165dff !important;
  border-color: #165dff !important;
  border-radius: 4px !important;
}

.pagination-wrapper :deep(.ant-pagination-item-active a) {
  color: #ffffff !important;
}

.pagination-wrapper :deep(.ant-pagination-item-active:hover) {
  background: #165dff !important;
  border-color: #165dff !important;
}

.pagination-wrapper :deep(.ant-pagination-item-active:hover a) {
  color: #ffffff !important;
}

/* 优化表格样式 */
.optimized-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: #fafafa;
  font-family: PingFang SC, PingFang SC;
  font-weight: bold;
  font-size: 14px;
  color: #333333;
}

.optimized-table :deep(.ant-table-tbody > tr > td) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 12px 8px;
  font-family: PingFang SC, PingFang SC !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  color: #333333 !important;
}

/* 备注列样式 - 单行显示，超出省略号 */
.optimized-table :deep(.ant-table-tbody > tr > td.remark-column-cell),
.optimized-table :deep(.ant-table-thead > tr > th.remark-column-cell) {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

/* 去掉表格边框 */
:deep(.ant-table) {
  border: none !important;
}

:deep(.ant-table-container) {
  border: none !important;
}

:deep(.ant-table-thead > tr > th) {
  border-bottom: 1px solid #f0f0f0 !important;
  border-right: none !important;
}

:deep(.ant-table-thead > tr > th:first-child) {
  border-left: none !important;
}

:deep(.ant-table-tbody > tr > td) {
  border-bottom: 1px solid #f0f0f0 !important;
  border-right: none !important;
}

:deep(.ant-table-tbody > tr > td:first-child) {
  border-left: none !important;
}

:deep(.ant-table-tbody > tr:last-child > td) {
  border-bottom: none !important;
}

/* 表格滚动条样式优化 */
:deep(.ant-table-body) {
  overflow-x: auto !important;
  scrollbar-width: thin;
  scrollbar-color: #d9d9d9 #f5f5f5;
}

:deep(.ant-table-body::-webkit-scrollbar) {
  height: 8px;
}

:deep(.ant-table-body::-webkit-scrollbar-track) {
  background: #f5f5f5;
  border-radius: 4px;
}

:deep(.ant-table-body::-webkit-scrollbar-thumb) {
  background: #d9d9d9;
  border-radius: 4px;
}

:deep(.ant-table-body::-webkit-scrollbar-thumb:hover) {
  background: #bfbfbf;
}

/* 序号列样式优化 */
:deep(.ant-table-tbody > tr > td:first-child) {
  font-family: PingFang SC, PingFang SC !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  color: #333333 !important;
}

/* 选中的行样式 - 去掉背景色 */
:deep(.ant-table-tbody > tr.ant-table-row-selected > td) {
  background-color: transparent !important;
}

:deep(.ant-table-tbody > tr.ant-table-row-selected:hover > td) {
  background-color: transparent !important;
}

/* 表格复选框样式 */
.optimized-table :deep(.ant-checkbox-inner) {
  width: 16px !important;
  height: 16px !important;
  border-radius: 0 !important;
  border-color: #999999 !important;
}

.optimized-table :deep(.ant-checkbox-checked .ant-checkbox-inner) {
  background-color: #165dff !important;
  border-color: #165dff !important;
}

.optimized-table :deep(.ant-checkbox-checked .ant-checkbox-inner::after) {
  border-color: #ffffff !important;
}

.optimized-table :deep(.ant-checkbox:hover .ant-checkbox-inner) {
  border-color: #165dff !important;
}

.optimized-table :deep(.ant-checkbox-input:focus + .ant-checkbox-inner) {
  border-color: #165dff !important;
}

.optimized-table :deep(.ant-checkbox-checked:hover .ant-checkbox-inner) {
  background-color: #165dff !important;
  border-color: #165dff !important;
}
</style>

<style>
/* 全局样式：确保备注列的 tooltip 能正确显示 */
.ant-tooltip {
  z-index: 9999 !important;
}

.ant-tooltip-inner {
  max-width: 600px !important;
  word-break: break-word !important;
  white-space: pre-wrap !important;
  max-height: 400px !important;
  overflow-y: auto !important;
  /* 隐藏滚动条但保持滚动功能 */
  scrollbar-width: none !important; /* Firefox */
  -ms-overflow-style: none !important; /* IE 10+ */
}

.ant-tooltip-inner::-webkit-scrollbar {
  display: none !important; /* Chrome/Safari */
  width: 0 !important;
  height: 0 !important;
}
</style>
